import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AddTransactionDialog, TransactionRulesDialog, TransactionDetailsDialog, TransferDialog } from "../components/Transactions";
import type { AddTransactionData, TransactionRule } from "../components/Transactions";
import { api, type ApiTransaction, type ApiAccount, type ApiCategory, type ApiJar } from "../services/api";
// import { useDebounce } from "../hooks/useDebounce"; // we'll make a tiny helper hook
import { Clear as ClearIcon } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabelIcon from "@mui/icons-material/Label";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CategoryFilterTree, { UNSELECTED_CATEGORY_ID } from "../components/Transactions/CategoryFilterTree";

/* ---------------------- Types ---------------------- */

type Transaction = {
  id: string;
  date: string;
  description: string;
  category: string;
  categoryId: string;
  account: string;
  accountId: string;
  amount: number;
  amountCents: number;
  currency: string;
  merchant: string | null;
  notes: string | null;
  jarIds: string[];
  jars: Array<{
    id: string;
    name: string;
  }>;
};

type SortKey = "date" | "description" | "category" | "account" | "amount";
type DisplayColumn = SortKey | "jars";

/* ---------------------- Helper Functions ---------------------- */

// Convert API transaction to UI transaction
function mapApiTransaction(apiTx: ApiTransaction): Transaction {
  return {
    id: apiTx.id,
    date: apiTx.occurredAt.split('T')[0], // Convert ISO to date
    description: apiTx.description,
    category: apiTx.category?.name ?? "Unselected",
    categoryId: apiTx.category?.id ?? "",
    account: apiTx.account.name,
    accountId: apiTx.account.id,
    amount: apiTx.amountCents / 100, // Convert cents to dollars
    amountCents: apiTx.amountCents,
    currency: apiTx.currency,
    merchant: apiTx.merchant,
    notes: apiTx.notes,
    jarIds: apiTx.allocations?.map(a => a.jar.id) ?? [],
    jars: apiTx.allocations?.map((allocation) => ({
      id: allocation.jar.id,
      name: allocation.jar.name,
    })) ?? [],
  };
}

// Format ISO date strings in a locale-aware, human-friendly way.
// Uses the user's default locale (undefined) so it appears correctly
// for international users (e.g., "7 Oct 2025" vs "Oct 7, 2025").
function formatDateIso(isoDate: string) {
  try {
    const d = new Date(isoDate);
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
  } catch (e) {
    return isoDate;
  }
}

/* ---------------------- Component ---------------------- */

export default function TransactionsPage({
  setHeaderActions,
}: {
  setHeaderActions: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}) {
  /* ---------- State ---------- */

  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [acctFilterAnchorEl, setAcctFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [jarFilterAnchorEl, setJarFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [jarFilterIds, setJarFilterIds] = useState<string[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<ApiAccount[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = React.useState<Transaction | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);

  // Multi-select / bulk-action state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [jars, setJars] = useState<ApiJar[]>([]);
  const [bulkCatAnchorEl, setBulkCatAnchorEl] = useState<HTMLElement | null>(null);
  const [bulkJarAnchorEl, setBulkJarAnchorEl] = useState<HTMLElement | null>(null);
  const [bulkCatSearch, setBulkCatSearch] = useState("");
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [newJarName, setNewJarName] = useState("");
  const [creatingJar, setCreatingJar] = useState(false);

  // Server-side pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const initialLoadDone = useRef(false);

  // Fetch transactions from server with current sort/filter/pagination params
  const fetchTransactions = useCallback(async (params: {
    sort: SortKey;
    direction: 'asc' | 'desc';
    search?: string;
    filters: {
      categoryIds: string[];
      accounts: string[];
      minAmount: number | null;
      maxAmount: number | null;
      startDate: string;
      endDate: string;
    };
    offset?: number;
    append?: boolean;
  }) => {
    if (params.append) setLoadingMore(true);
    try {
      const resp = await api.getTransactions({
        limit: 200,
        offset: params.offset ?? 0,
        sort: params.sort,
        direction: params.direction,
        search: params.search || undefined,
        categoryIds: params.filters.categoryIds.length > 0 ? params.filters.categoryIds : undefined,
        accounts: params.filters.accounts.length > 0 ? params.filters.accounts : undefined,
        minAmount: params.filters.minAmount ?? undefined,
        maxAmount: params.filters.maxAmount ?? undefined,
        startDate: params.filters.startDate || undefined,
        endDate: params.filters.endDate || undefined,
      });
      const mapped = resp.transactions.map(mapApiTransaction);
      if (params.append) {
        setTransactions(prev => [...prev, ...mapped]);
      } else {
        setTransactions(mapped);
      }
      setTotalCount(resp.total);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  const handleRowClick = (tx: Transaction, e: React.MouseEvent) => {
    const idx = transactions.findIndex((t) => t.id === tx.id);

    if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd+click — toggle individual row
      e.preventDefault();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(tx.id)) next.delete(tx.id);
        else next.add(tx.id);
        return next;
      });
      lastClickedIdxRef.current = idx;
      return;
    }

    if (e.shiftKey) {
      // Shift+click — select range from last clicked to here
      e.preventDefault();
      const last = lastClickedIdxRef.current;
      const from = last >= 0 ? Math.min(last, idx) : idx;
      const to = last >= 0 ? Math.max(last, idx) : idx;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        transactions.slice(from, to + 1).forEach((t) => next.add(t.id));
        return next;
      });
      return;
    }

    // Plain click — update anchor and open detail dialog
    lastClickedIdxRef.current = idx;
    setSelectedTransaction(tx);
    setIsDetailsOpen(true);
  };

  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: "asc" | "desc" }>({
    key: "date",
    direction: "desc",
  });

  const [filters, setFilters] = useState({
    categoryIds: [] as string[],
    accounts: [] as string[],
    minAmount: null as number | null,
    maxAmount: null as number | null,
    startDate: "",
    endDate: "",
  });
  const activeFilters = React.useMemo(() => {
    return (
      filters.categoryIds.length > 0 ||
      filters.accounts.length > 0 ||
      filters.minAmount !== null ||
      filters.maxAmount !== null ||
      filters.startDate !== "" ||
      filters.endDate !== ""
    );
  }, [filters]);
  const activeJarFilter = jarFilterIds.length > 0;
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rules, setRules] = useState<TransactionRule[]>([]);

  const handleExportCsv = useCallback(() => {
    void api.exportTransactionsCsv({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    }).catch((err) => {
      console.error("Error exporting transactions CSV:", err);
    });
  }, [filters.endDate, filters.startDate]);

  /* ---------- Data Fetching ---------- */

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [txResponse, accountsResponse, categoriesResponse, rulesResponse, jarsResponse] = await Promise.all([
          api.getTransactions({ limit: 200, sort: 'date', direction: 'desc' }),
          api.getAccounts(),
          api.getCategories(),
          api.getTransactionRules(),
          api.getJars(),
        ]);

        setTransactions(txResponse.transactions.map(mapApiTransaction));
        setTotalCount(txResponse.total);
        setAccounts(accountsResponse.accounts);
        setCategories(categoriesResponse.categories);
        setRules(rulesResponse.rules);
        setJars(jarsResponse.jars);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
        initialLoadDone.current = true;
      }
    };

    fetchData();
  }, []);

  /* ---------- Derived Lists ---------- */

  const allCategoryNames = useMemo(
    () =>
      Array.from(
        new Set([
          ...categories.map((c) => c.name),
          ...rules.map((r) => r.categoryName),
        ])
      ),
    [categories, rules]
  );

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [categories]);

  const selectedCategoryNames = useMemo(() => {
    const names = filters.categoryIds
      .filter((id) => id !== UNSELECTED_CATEGORY_ID)
      .map((id) => categoryNameById.get(id) ?? id);
    if (filters.categoryIds.includes(UNSELECTED_CATEGORY_ID)) names.unshift("Unselected");
    return names;
  }, [filters.categoryIds, categoryNameById]);
  const allAccounts = useMemo(() => accounts.map(a => a.name), [accounts]);

  const visibleTransactions = useMemo(() => {
    if (jarFilterIds.length === 0) return transactions;
    return transactions.filter(tx => tx.jarIds.some(id => jarFilterIds.includes(id)));
  }, [transactions, jarFilterIds]);

  /* ---------- Header Actions ---------- */


  useEffect(() => {
    setHeaderActions(
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleExportCsv}
          sx={{ width: 168, whiteSpace: "nowrap", textTransform: "none", px: 1.5, py: 0.75 }}
        >
          Export CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<RuleOutlinedIcon />}
          onClick={() => setIsRulesOpen(true)}
          sx={{ width: 168, whiteSpace: "nowrap", textTransform: "none", px: 1.5, py: 0.75 }}
        >
          Category Rules
        </Button>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setIsAddOpen(true)}
          sx={{ width: 168, whiteSpace: "nowrap", textTransform: "none", px: 1.5, py: 0.75 }}
        >
          Add Transaction
        </Button>
        {/* <Button variant="outlined" onClick={() => setIsTransferOpen(true)}>
          ⇄ Transfer
        </Button> */}
      </Stack>
    );
    return () => setHeaderActions(null);
  }, [handleExportCsv, setHeaderActions]);


  /* ---------- Server-side Search (debounced) ---------- */

  useEffect(() => {
    if (!initialLoadDone.current) return;
    const timer = setTimeout(() => {
      fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  /* ---------- Sorting (server-side) ---------- */

  const handleSort = (key: SortKey) => {
    const newDir = (sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc") as "asc" | "desc";
    setSortConfig({ key, direction: newDir });
    fetchTransactions({ sort: key, direction: newDir, search: searchTerm, filters });
  };

  /* ---------- Selection ---------- */

  const lastClickedIdxRef = React.useRef<number>(-1);

  const allVisibleIds = useMemo(() => visibleTransactions.map((t) => t.id), [visibleTransactions]);
  const allVisibleSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    allVisibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const handleToggleSelectAll = React.useCallback(() => {
    if (allVisibleSelected || someVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [allVisibleIds, allVisibleSelected, someVisibleSelected]);

  const resolveCategoryForNewTx = React.useCallback(
    (tx: AddTransactionData): { categoryId: string | null; categoryName: string } => {
      // Explicit selection wins
      if (tx.category && tx.category !== 'Unselected') {
        const cat = categories.find((c) => c.name === tx.category);
        if (cat) return { categoryId: cat.id, categoryName: cat.name };
        // fall through to rules if the name can't be resolved
      }

      // Only apply rules when category is unselected/unknown
      const match = rules.find((rule) => {
        const fieldValue =
          rule.field === 'description'
            ? tx.description
            : rule.field === 'account'
              ? tx.account
              : tx.category;

        const fieldString = String(fieldValue ?? '');
        const ruleString = rule.value;
        const fv = rule.caseSensitive ? fieldString : fieldString.toLowerCase();
        const rv = rule.caseSensitive ? ruleString : ruleString.toLowerCase();
        if (rule.operator === 'contains') return fv.includes(rv);
        if (rule.operator === 'equals') return fv === rv;
        return false;
      });

      if (match) return { categoryId: match.categoryId, categoryName: match.categoryName };

      return { categoryId: null, categoryName: 'Unselected' };
    },
    [categories, rules]
  );

  const handleCreateRule = React.useCallback(
    async (rule: Omit<TransactionRule, 'id' | 'categoryName'>) => {
      const resp = await api.createTransactionRule(rule);
      setRules((prev) => [resp.rule, ...prev]);
    },
    []
  );

  const handleUpdateRule = React.useCallback(
    async (id: string, rule: Omit<TransactionRule, 'id' | 'categoryName'>) => {
      const resp = await api.updateTransactionRule(id, rule);
      setRules((prev) => prev.map((r) => (r.id === id ? resp.rule : r)));
    },
    []
  );

  const handleDeleteRule = React.useCallback(async (id: string) => {
    await api.deleteTransactionRule(id);
    setRules((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handlePreviewRule = React.useCallback(
    async (rule: Omit<TransactionRule, 'id' | 'categoryName'> & { onlyUncategorized: boolean }) => {
      return api.previewTransactionRule({
        ...rule,
        caseSensitive: rule.caseSensitive,
        onlyUncategorized: rule.onlyUncategorized,
        limit: 50,
      });
    },
    []
  );

  const handleApplyRule = React.useCallback(
    async (rule: Omit<TransactionRule, 'id' | 'categoryName'> & { onlyUncategorized: boolean }) => {
      const result = await api.applyTransactionRule({
        ...rule,
        caseSensitive: rule.caseSensitive,
        onlyUncategorized: rule.onlyUncategorized,
      });

      // Refresh transactions after applying so UI reflects DB
      await fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });

      return { updatedCount: result.updatedCount };
    },
    []
  );

  /* ---------- Bulk Actions ---------- */

  const handleBulkDelete = async () => {
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      await api.bulkDeleteTransactions(ids);
      setTransactions((prev) => prev.filter((t) => !selectedIds.has(t.id)));
      setSelectedIds(new Set());
      setIsBulkDeleteConfirmOpen(false);
    } catch (err) {
      console.error("Bulk delete error:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSetCategory = async (categoryId: string | null, categoryName: string) => {
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      await api.bulkSetCategory(ids, categoryId);
      setTransactions((prev) =>
        prev.map((t) =>
          selectedIds.has(t.id)
            ? { ...t, category: categoryName, categoryId: categoryId ?? "" }
            : t
        )
      );
      setBulkCatAnchorEl(null);
      setBulkCatSearch("");
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Bulk set category error:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAssignJar = async (jarId: string) => {
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      await api.bulkAssignJar(ids, jarId);
      // Refresh so allocations are reflected in the detail view
      await fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });
      setBulkJarAnchorEl(null);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Bulk assign jar error:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCreateAndAssignJar = async () => {
    const name = newJarName.trim();
    if (!name) return;
    setCreatingJar(true);
    try {
      const { jar } = await api.createJar({ name });
      setJars(prev => [...prev, jar]);
      await handleBulkAssignJar(jar.id);
      setNewJarName("");
    } catch (err) {
      console.error("Create jar error:", err);
    } finally {
      setCreatingJar(false);
    }
  };

  const handleDeleteSingle = async (id: string) => {
    await api.deleteTransaction(id);
    setTransactions((prev) => prev.filter((t) => t.id !== id));
    setIsDetailsOpen(false);
    setSelectedTransaction(null);
  };

  const handleSaveTransaction = async (
    id: string,
    patch: { notes?: string | null; merchant?: string | null; description?: string }
  ) => {
    const { transaction: updated } = await api.updateTransaction(id, patch);
    // Update local state so the list reflects the edit
    setTransactions((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              notes: updated.notes,
              merchant: updated.merchant,
              description: updated.description ?? t.description,
            }
          : t
      )
    );
    setSelectedTransaction((prev) =>
      prev && prev.id === id
        ? {
            ...prev,
            notes: updated.notes,
            merchant: updated.merchant,
            description: updated.description ?? prev.description,
          }
        : prev
    );
  };

  /* ---------- Grouping ---------- */

  const isGrouped = sortConfig.key === "date";
  const groupedTransactions = useMemo(() => {
    if (!isGrouped) return { flat: visibleTransactions };
    return visibleTransactions.reduce<Record<string, Transaction[]>>((acc, tx) => {
      (acc[tx.date] ??= []).push(tx);
      return acc;
    }, {});
  }, [visibleTransactions, isGrouped]);

  const sortedDates = isGrouped
    ? Object.keys(groupedTransactions).sort((a, b) =>
      sortConfig.direction === "asc" ? a.localeCompare(b) : b.localeCompare(a)
    )
    : ["flat"];

  /* ---------- Handlers ---------- */

  const handleAddTransaction = async (tx: AddTransactionData) => {
    try {
      // Find account
      const account = accounts.find(a => a.name === tx.account);
      const resolved = resolveCategoryForNewTx(tx);

      if (!account) {
        throw new Error(`Account "${tx.account}" not found`);
      }

      // Create transaction via API
      const response = await api.createTransaction({
        accountId: account.id,
        categoryId: resolved.categoryId ?? undefined,
        occurredAt: new Date(tx.date).toISOString(),
        description: tx.description,
        amountCents: Math.round(tx.amount * 100),
        currency: tx.currency,
      });

      // Manually construct the transaction object since API response doesn't include account/category names
      const newTransaction: Transaction = {
        id: response.transaction.id,
        date: response.transaction.occurredAt.split('T')[0],
        description: response.transaction.description,
        category: resolved.categoryName,
        categoryId: resolved.categoryId ?? "",
        account: account.name,
        accountId: account.id,
        amount: response.transaction.amountCents / 100,
        amountCents: response.transaction.amountCents,
        currency: response.transaction.currency,
        merchant: response.transaction.merchant,
        notes: response.transaction.notes,
        jarIds: [],
        jars: [],
      };
    } catch (err) {
      console.error('Error creating transaction:', err);
      alert(`Failed to create transaction: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleUpdateCategory = (id: string, categoryName: string) => {
    const prevTx = transactions.find((t) => t.id === id);

    // Optimistic UI update
    if (categoryName === "") {
      setTransactions((prev) => prev.map((t) => (t.id === id ? { ...t, category: "Unselected", categoryId: "" } : t)));
    } else {
      const category = categories.find((c) => c.name === categoryName);
      if (!category) return;
      setTransactions((prev) => prev.map((t) => (t.id === id ? { ...t, category: categoryName, categoryId: category.id } : t)));
    }

    // Persist to API
    (async () => {
      try {
        if (categoryName === "") {
          await api.updateTransactionCategory(id, null);
          return;
        }

        const category = categories.find((c) => c.name === categoryName);
        if (!category) throw new Error(`Category "${categoryName}" not found`);
        await api.updateTransactionCategory(id, category.id);
      } catch (err) {
        console.error('Failed to update category:', err);
        // revert on failure
        if (prevTx) {
          setTransactions((prev) => prev.map((t) => (t.id === id ? prevTx : t)));
        }
        alert(`Failed to update category: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    })();
  };

  const handleBulkAddTransactions = async (txs: AddTransactionData[]) => {
    try {
      // Build bulk API request
      const bulkData = txs.map(tx => {
        const account = accounts.find(a => a.name === tx.account);
        const resolved = resolveCategoryForNewTx(tx);

        if (!account) {
          throw new Error(`Account "${tx.account}" not found`);
        }

        return {
          accountId: account.id,
          categoryId: resolved.categoryId ?? undefined,
          occurredAt: new Date(tx.date).toISOString(),
          description: tx.description,
          amountCents: Math.round(tx.amount * 100),
          currency: tx.currency,
        };
      });

      // Call bulk API
      const response = await api.createTransactionsBulk(bulkData);

      // Map responses back to Transaction objects
      const newTransactions: Transaction[] = response.transactions.map((apiTx, index) => {
        const originalTx = txs[index];
        const account = accounts.find(a => a.name === originalTx.account);
        const resolved = resolveCategoryForNewTx(originalTx);

        return {
          id: apiTx.id,
          date: apiTx.occurredAt.split('T')[0],
          description: apiTx.description,
          category: resolved.categoryName,
          categoryId: resolved.categoryId ?? '',
          account: account?.name ?? originalTx.account,
          accountId: account?.id ?? '',
          amount: apiTx.amountCents / 100,
          amountCents: apiTx.amountCents,
          currency: apiTx.currency,
          merchant: apiTx.merchant,
          notes: apiTx.notes,
          jarIds: [],
          jars: [],
        };
      });

      // Add to local state
      setTransactions((prev) => [...newTransactions, ...prev]);
    } catch (err) {
      console.error('Error bulk creating transactions:', err);
      throw new Error(`Failed to bulk create transactions: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const clearFilters = () => {
    const emptyFilters = {
      categoryIds: [] as string[],
      accounts: [] as string[],
      minAmount: null as number | null,
      maxAmount: null as number | null,
      startDate: "",
      endDate: "",
    };
    setFilters(emptyFilters);
    setJarFilterIds([]);
    fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters: emptyFilters });
  };

  /* ---------------------- Render ---------------------- */

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        pt: 2,
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 1, sm: 1.5 },
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* --- Add Transaction Dialog --- */}
      <AddTransactionDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdd={handleAddTransaction}
        onBulkAdd={handleBulkAddTransactions}
        availableAccounts={allAccounts}
        accountObjects={accounts.map(a => ({ name: a.name, behavior: a.behavior }))}
        availableCategories={allCategoryNames}
        defaultCurrency="USD"
        existingTransactions={transactions.map(tx => ({
          date: tx.date,
          description: tx.description,
          amount: tx.amount
        }))}
        transactionRules={rules}
      />

      {/*Rules Dialog */}
      <TransactionRulesDialog
        open={isRulesOpen}
        onClose={() => setIsRulesOpen(false)}
        rules={rules}
        onCreateRule={handleCreateRule}
        onUpdateRule={handleUpdateRule}
        onDeleteRule={handleDeleteRule}
        onPreviewRule={handlePreviewRule}
        onApplyRule={handleApplyRule}
        availableCategories={categories.map((c) => ({ id: c.id, name: c.name }))}
        availableAccounts={allAccounts}
      />

      {/* --- Transaction Details Dialog --- */}
      <TransactionDetailsDialog
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        transaction={selectedTransaction}
        onDelete={handleDeleteSingle}
        onSave={handleSaveTransaction}
      />

      {/* --- Transfer Dialog --- */}
      <TransferDialog
        open={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        accounts={accounts}
        onSuccess={() => fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters })}
      />

      {/* --- Bulk: Delete Confirm Dialog --- */}
      <Dialog
        open={isBulkDeleteConfirmOpen}
        onClose={() => !bulkLoading && setIsBulkDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete {selectedIds.size} transaction{selectedIds.size !== 1 ? "s" : ""}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete {selectedIds.size} selected transaction
            {selectedIds.size !== 1 ? "s" : ""} and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBulkDeleteConfirmOpen(false)} disabled={bulkLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBulkDelete}
            disabled={bulkLoading}
          >
            {bulkLoading ? <CircularProgress size={16} /> : `Delete ${selectedIds.size}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Bulk: Category Picker Menu --- */}
      <Menu
        anchorEl={bulkCatAnchorEl}
        open={Boolean(bulkCatAnchorEl)}
        autoFocus={false}
        disableAutoFocusItem
        onClose={() => { setBulkCatAnchorEl(null); setBulkCatSearch(""); }}
      >
        <Box
          sx={{ p: 1, pb: 0.5, width: 260 }}
          onClick={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search categories..."
            value={bulkCatSearch}
            onChange={(e) => setBulkCatSearch(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") { setBulkCatAnchorEl(null); setBulkCatSearch(""); }
            }}
          />
        </Box>
        <MenuItem onClick={() => handleBulkSetCategory(null, "Unselected")} disabled={bulkLoading}>
          Unselected
        </MenuItem>
        {categories
          .filter((c) => !bulkCatSearch || c.name.toLowerCase().includes(bulkCatSearch.toLowerCase()))
          .map((c) => (
            <MenuItem key={c.id} onClick={() => handleBulkSetCategory(c.id, c.name)} disabled={bulkLoading}>
              {c.name}
            </MenuItem>
          ))}
      </Menu>

      {/* --- Bulk: Jar Picker Menu --- */}
      <Menu
        anchorEl={bulkJarAnchorEl}
        open={Boolean(bulkJarAnchorEl)}
        onClose={() => { setBulkJarAnchorEl(null); setNewJarName(""); }}
      >
        {jars.length === 0 ? (
          <MenuItem disabled>No jars available</MenuItem>
        ) : (
          jars.map((jar) => (
            <MenuItem key={jar.id} onClick={() => handleBulkAssignJar(jar.id)} disabled={bulkLoading}>
              {jar.name}
            </MenuItem>
          ))
        )}
        <Divider />
        <Box sx={{ px: 1.5, py: 1 }} onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            placeholder="New jar name…"
            size="small"
            fullWidth
            value={newJarName}
            onChange={(e) => setNewJarName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newJarName.trim()) handleCreateAndAssignJar();
            }}
            sx={{ mb: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            fullWidth
            disabled={!newJarName.trim() || creatingJar || bulkLoading}
            onClick={handleCreateAndAssignJar}
          >
            {creatingJar ? "Creating…" : "Create & Assign"}
          </Button>
        </Box>
      </Menu>

      {/* --- Search & Filter Bar --- */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pt: 2, mb: 2, gap: 1, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          placeholder="Search transactions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 0.75 }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{
            width: 260,
            '& .MuiOutlinedInput-root': {
              height: 32,
              bgcolor: 'transparent',
            },
            '& .MuiOutlinedInput-input': {
              py: 0.5,
            },
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {(activeFilters || activeJarFilter) && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
              Clear
            </Button>
          )}
          <Button
            size="small"
            variant={(filters.startDate || filters.endDate) ? "contained" : "outlined"}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setDateAnchorEl(e.currentTarget)}
          >
            Date
          </Button>
          <Button
            size="small"
            variant={filters.accounts.length > 0 ? "contained" : "outlined"}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setAcctFilterAnchorEl(e.currentTarget)}
          >
            Accounts
          </Button>
          <Button
            size="small"
            variant={activeJarFilter ? "contained" : "outlined"}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setJarFilterAnchorEl(e.currentTarget)}
          >
            Jars
          </Button>
          <Button
            size="small"
            variant={activeFilters ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            All Filters
          </Button>
        </Stack>
      </Stack>

      {/* --- Reset to Date Grouping --- */}
      {!isGrouped && (
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Button variant="contained" size="small" onClick={() => setSortConfig({ key: "date", direction: "desc" })}>
            Reset to Date Grouping
          </Button>
        </Box>
      )}

      {/* --- Date Filter Menu --- */}
      <Menu
        open={!!dateAnchorEl}
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        slotProps={{ paper: { sx: { p: 2, width: 280 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Date Range</Typography>
        <Stack spacing={1.5}>
          <TextField
            type="date"
            label="Start"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={(e) => setFilters(p => ({ ...p, startDate: e.target.value }))}
          />
          <TextField
            type="date"
            label="End"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={(e) => setFilters(p => ({ ...p, endDate: e.target.value }))}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setFilters(p => ({ ...p, startDate: '', endDate: '' }))}>Clear</Button>
            <Button size="small" variant="contained" onClick={() => {
              setDateAnchorEl(null);
              fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });
            }}>Apply</Button>
          </Stack>
        </Stack>
      </Menu>

      {/* --- Accounts Filter Menu --- */}
      <Menu
        open={!!acctFilterAnchorEl}
        anchorEl={acctFilterAnchorEl}
        onClose={() => {
          setAcctFilterAnchorEl(null);
          fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });
        }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 220 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Accounts</Typography>
        {allAccounts.map((acct) => (
          <FormControlLabel
            key={acct}
            control={
              <Checkbox
                size="small"
                checked={filters.accounts.includes(acct)}
                onChange={(e) => {
                  setFilters(prev => ({
                    ...prev,
                    accounts: e.target.checked
                      ? [...prev.accounts, acct]
                      : prev.accounts.filter(a => a !== acct),
                  }));
                }}
              />
            }
            label={acct}
            sx={{ display: 'flex', mx: 0 }}
          />
        ))}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button size="small" onClick={() => {
            const updated = { ...filters, accounts: [] };
            setFilters(updated);
            fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters: updated });
            setAcctFilterAnchorEl(null);
          }}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => {
            setAcctFilterAnchorEl(null);
            fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });
          }}>Apply</Button>
        </Stack>
      </Menu>

      {/* --- Jars Filter Menu --- */}
      <Menu
        open={!!jarFilterAnchorEl}
        anchorEl={jarFilterAnchorEl}
        onClose={() => setJarFilterAnchorEl(null)}
        slotProps={{ paper: { sx: { p: 2, minWidth: 220 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter by Jar</Typography>
        {jars.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No jars available</Typography>
        ) : jars.map((jar) => (
          <FormControlLabel
            key={jar.id}
            control={
              <Checkbox
                size="small"
                checked={jarFilterIds.includes(jar.id)}
                onChange={(e) => {
                  setJarFilterIds(prev =>
                    e.target.checked ? [...prev, jar.id] : prev.filter(id => id !== jar.id)
                  );
                }}
              />
            }
            label={jar.name}
            sx={{ display: 'flex', mx: 0 }}
          />
        ))}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setJarFilterIds([])}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => setJarFilterAnchorEl(null)}>Done</Button>
        </Stack>
      </Menu>

      {/* --- Filter Menu --- */}
      <Menu
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        slotProps={{ paper: { sx: { width: 300, p: 2 } } }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Filter Transactions</Typography>
        <Divider sx={{ mb: 1 }} />

        {/* Categories */}
        <Typography variant="body2" fontWeight={500}>Categories</Typography>
        <CategoryFilterTree
          categories={categories}
          selectedCategoryIds={filters.categoryIds}
          onChange={(nextIds) => setFilters((p) => ({ ...p, categoryIds: nextIds }))}
        />

        <Divider sx={{ my: 1 }} />

        {/* Accounts */}
        <Typography variant="body2" fontWeight={500}>Accounts</Typography>
        {allAccounts.map((acct) => (
          <FormControlLabel
            key={acct}
            control={
              <Checkbox
                checked={filters.accounts.includes(acct)}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    accounts: e.target.checked
                      ? [...prev.accounts, acct]
                      : prev.accounts.filter((a) => a !== acct),
                  }));
                }}
              />
            }
            label={acct}
          />
        ))}

        <Divider sx={{ my: 1 }} />

        {/* Amount Range */}
        <Typography variant="body2" fontWeight={500}>Amount Range</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            label="Min"
            type="number"
            size="small"
            fullWidth
            value={filters.minAmount ?? ""}
            onChange={(e) => setFilters((p) => ({ ...p, minAmount: e.target.value ? Number(e.target.value) : null }))}
          />
          <TextField
            label="Max"
            type="number"
            size="small"
            fullWidth
            value={filters.maxAmount ?? ""}
            onChange={(e) => setFilters((p) => ({ ...p, maxAmount: e.target.value ? Number(e.target.value) : null }))}
          />
        </Stack>

        <Divider sx={{ my: 1 }} />

        {/* Date Range */}
        <Typography variant="body2" fontWeight={500}>Date Range</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            type="date"
            label="Start"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
          />
          <TextField
            type="date"
            label="End"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
          />
        </Stack>

        <Divider sx={{ my: 1 }} />

        {/* Menu Buttons */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters}>Clear</Button>
          <Button variant="contained" onClick={() => {
            setAnchorEl(null);
            fetchTransactions({ sort: sortConfig.key, direction: sortConfig.direction, search: searchTerm, filters });
          }}>Apply</Button>
        </Stack>
      </Menu>

      {/* --- Active Filters Display --- */}
      {(activeFilters || activeJarFilter) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[800],
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing filtered results:
            {filters.categoryIds.length > 0 && ` Categories (${selectedCategoryNames.join(", ")})`}
            {filters.accounts.length > 0 && ` | Accounts (${filters.accounts.join(", ")})`}
            {(filters.minAmount !== null || filters.maxAmount !== null) &&
              ` | Amount (${filters.minAmount ?? "–"}–${filters.maxAmount ?? "–"})`}
            {(filters.startDate || filters.endDate) &&
              ` | Date (${filters.startDate || "…"} – ${filters.endDate || "…"})`}
            {activeJarFilter && ` | Jars (${jars.filter(j => jarFilterIds.includes(j.id)).map(j => j.name).join(", ")})`}
          </Typography>
          <Button size="small" onClick={clearFilters}>Clear Filters</Button>
        </Box>
      )}


      {/* --- Bulk Selection Toolbar --- */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 1,
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.main",
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "rgba(25,118,210,0.06)"
                : "rgba(25,118,210,0.18)",
          }}
        >
          <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mr: 0.5 }}>
            {selectedIds.size} selected
          </Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Set category for selected">
            <Button
              size="small"
              startIcon={<LabelIcon fontSize="small" />}
              onClick={(e) => { setBulkCatAnchorEl(e.currentTarget); setBulkCatSearch(""); }}
              disabled={bulkLoading}
            >
              Set Category
            </Button>
          </Tooltip>
          <Tooltip title="Assign selected to a jar">
            <Button
              size="small"
              startIcon={<AccountBalanceWalletIcon fontSize="small" />}
              onClick={(e) => setBulkJarAnchorEl(e.currentTarget)}
              disabled={bulkLoading}
            >
              Assign to Jar
            </Button>
          </Tooltip>
          <Tooltip title="Delete selected transactions">
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              onClick={() => setIsBulkDeleteConfirmOpen(true)}
              disabled={bulkLoading}
            >
              Delete
            </Button>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Clear selection">
            <IconButton size="small" onClick={() => setSelectedIds(new Set())}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      {/* --- Transactions Table --- */}
      <Box
        sx={{
          pb: { xs: 1, sm: 1.5 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            borderRadius: { xs: 2, sm: 3 },
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: 'fixed',
              minWidth: isMobile ? 620 : isTablet ? 720 : 860,
              '& .MuiTableCell-stickyHeader': {
                top: 0,
                zIndex: 2,
                backgroundColor: 'background.paper',
                boxShadow: (theme) => `inset 0 -1px 0 ${theme.palette.divider}`,
              },
            }}
          >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "background.paper",
              }}
            >
              <TableCell padding="checkbox" sx={{ width: 40 }}>
                <Checkbox
                  size="small"
                  indeterminate={someVisibleSelected}
                  checked={allVisibleSelected}
                  onChange={handleToggleSelectAll}
                />
              </TableCell>
              {((isGrouped
                ? ["description", "category", "jars", "amount"]
                : ["date", "description", "category", "jars", "amount"]) as DisplayColumn[]).map((key) => {
                const align = key === "amount" ? "right" : "left" as const;
                const headerSx: any = {
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "text.secondary",
                  whiteSpace: 'nowrap',
                  px: isMobile ? 1 : 2,
                  py: isMobile ? 1 : 1.5,
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                };
                if (key === "date") headerSx.width = isMobile ? 112 : 140;
                else if (key === "description") {
                  headerSx.width = isGrouped ? (isTablet ? '36%' : '39%') : (isTablet ? '32%' : '34%');
                  headerSx.minWidth = isMobile ? 170 : isTablet ? 210 : 240;
                } else if (key === "category") {
                  headerSx.width = isMobile ? 132 : isTablet ? 150 : 180;
                } else if (key === "jars") {
                  headerSx.width = isMobile ? 150 : isTablet ? 180 : 240;
                } else if (key === "amount") {
                  headerSx.width = isMobile ? 108 : 140;
                }

                return (
                  <TableCell
                    key={key}
                    align={align}
                    sx={headerSx}
                  >
                    {key === "jars" ? (
                      isMobile ? 'Jars' : 'Jar assignment'
                    ) : (
                      <TableSortLabel
                        active={sortConfig.key === key}
                        direction={sortConfig.direction}
                        onClick={() => handleSort(key as SortKey)}
                      >
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </TableSortLabel>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isGrouped ? (
              sortedDates.map((date) => (
                <React.Fragment key={date}>
                  <TableRow sx={{ backgroundColor: '#E6FBF3' }}>
                    <TableCell
                      colSpan={5}
                      sx={{
                        py: isMobile ? 1 : 1.25,
                        px: isMobile ? 1.25 : 2,
                        fontWeight: 700,
                        color: '#04503A',
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        fontSize: isMobile ? '0.75rem' : '0.8125rem',
                        borderBottom: '1px solid',
                        borderColor: 'rgba(4, 80, 58, 0.08)',
                      }}
                    >
                      {formatDateIso(date)}
                    </TableCell>
                  </TableRow>
                  {groupedTransactions[date]?.map((tx) => (
                    <TransactionRow
                      key={tx.id}
                      tx={tx}
                      onClick={handleRowClick}
                      availableCategories={allCategoryNames}
                      onUpdateCategory={handleUpdateCategory}
                      isGrouped={isGrouped}
                      isSelected={selectedIds.has(tx.id)}
                      isCompact={isTablet}
                      isMobile={isMobile}
                    />
                  ))}
                </React.Fragment>
              ))
            ) : (
              visibleTransactions.map((tx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  onClick={handleRowClick}
                  availableCategories={allCategoryNames}
                  onUpdateCategory={handleUpdateCategory}
                  isGrouped={false}
                  isSelected={selectedIds.has(tx.id)}
                  isCompact={isTablet}
                  isMobile={isMobile}
                />
              ))
            )}
            <TableRow aria-hidden>
              <TableCell colSpan={isGrouped ? 5 : 6} sx={{ border: 0, py: 1 }} />
            </TableRow>
          </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* --- Pagination Info & Load More --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 0.75, sm: 1.25 },
          pt: 0.25,
          pb: 0.25,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {visibleTransactions.length} of {totalCount} transactions
        </Typography>
        {transactions.length < totalCount && (
          <Button
            variant="outlined"
            size="small"
            disabled={loadingMore}
            onClick={() =>
              fetchTransactions({
                sort: sortConfig.key,
                direction: sortConfig.direction,
                search: searchTerm,
                filters,
                offset: transactions.length,
                append: true,
              })
            }
          >
            {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Load More
          </Button>
        )}
      </Box>
    </Box>
  );
}

/* ---------------------- Subcomponent ---------------------- */

function TransactionRow({
  tx,
  onClick,
  availableCategories,
  onUpdateCategory,
  isGrouped = false,
  isSelected = false,
  isCompact = false,
  isMobile = false,
}: {
  tx: Transaction;
  onClick: (tx: Transaction, e: React.MouseEvent) => void;
  availableCategories: string[];
  onUpdateCategory: (id: string, category: string) => void;
  isGrouped?: boolean;
  isSelected?: boolean;
  isCompact?: boolean;
  isMobile?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hover, setHover] = useState(false);
  const [categoryMenuSearch, setCategoryMenuSearch] = useState("");

  const filteredCategories = useMemo(() => {
    const q = categoryMenuSearch.trim().toLowerCase();
    if (!q) return availableCategories;
    return availableCategories.filter((c) => c.toLowerCase().includes(q));
  }, [availableCategories, categoryMenuSearch]);

  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setCategoryMenuSearch("");
  };
  const handleClose = (e?: React.MouseEvent) => {
    try {
      e?.stopPropagation();
    } catch { }
    setAnchorEl(null);
    setCategoryMenuSearch("");
  };

  const handleSelect = (cat: string) => {
    onUpdateCategory(tx.id, cat);
    setAnchorEl(null);
  };

  return (
    <TableRow
      key={tx.id}
      hover
      selected={isSelected}
      onClick={(e) => onClick(tx, e)}
      sx={{
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {/* Spacer cell aligns with header select-all checkbox */}
      <TableCell sx={{ width: isMobile ? 32 : 40, p: 0, border: 0 }} />

      {!isGrouped && (
        <TableCell
          sx={{
            width: isMobile ? 112 : 120,
            whiteSpace: "nowrap",
            border: 0,
            py: isCompact ? 1.25 : 1.5,
            px: isMobile ? 1 : 2,
            fontSize: isMobile ? '0.75rem' : undefined,
          }}
        >
          {formatDateIso(tx.date)}
        </TableCell>
      )}

      <TableCell
        sx={{
          maxWidth: isGrouped ? (isCompact ? 360 : 520) : (isCompact ? 300 : 480),
          border: 0,
          py: isCompact ? 1.25 : 1.5,
          px: isMobile ? 1 : 2,
        }}
      >
        <Stack spacing={0.35} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.85rem' : undefined }} noWrap>
            {tx.description}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : undefined }} noWrap>
            {tx.account}
          </Typography>
        </Stack>
      </TableCell>

      <TableCell sx={{ width: isMobile ? 132 : isCompact ? 150 : 180, border: 0, py: isCompact ? 1.25 : 1.5, px: isMobile ? 1 : 2 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", width: "100%" }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={(e) => {
            e.stopPropagation();
            handleOpen(e as unknown as React.MouseEvent<HTMLElement>);
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: isMobile ? 1 : 1.25,
              py: isMobile ? 0.45 : 0.55,
              borderRadius: 999,
              border: '1px solid',
              borderColor: tx.category === 'Unselected' ? 'rgba(237, 108, 2, 0.35)' : 'rgba(4, 80, 58, 0.18)',
              backgroundColor: tx.category === 'Unselected' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(4, 80, 58, 0.08)',
              color: tx.category === 'Unselected' ? 'warning.dark' : '#04503A',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: isMobile ? '0.68rem' : undefined }}>
              {tx.category}
            </Typography>
            <ArrowDropDownIcon fontSize="inherit" sx={{ opacity: hover ? 0.9 : 0.55 }} />
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          autoFocus={false}
          disableAutoFocusItem
          onClose={(event: any) => {
            try {
              event?.stopPropagation?.();
            } catch { }
            handleClose();
          }}
        >
          <Box
            sx={{ p: 1, pt: 1, pb: 0.5, width: 260 }}
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search categories..."
              value={categoryMenuSearch}
              onChange={(e) => setCategoryMenuSearch(e.target.value)}
              onKeyDown={(e) => {
                // Prevent the Menu from handling keystrokes as "type to select"
                // while still allowing Escape to close the menu.
                e.stopPropagation();
                if (e.key === "Escape") handleClose(e as any);
              }}
              InputProps={{
                endAdornment: categoryMenuSearch ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategoryMenuSearch("");
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
          </Box>

          <MenuItem
            key="unselected"
            selected={"Unselected" === tx.category}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect("");
            }}
          >
            Unselected
          </MenuItem>

          {filteredCategories.length === 0 ? (
            <MenuItem disabled onClick={(e) => e.stopPropagation()}>
              No matches
            </MenuItem>
          ) : null}

          {filteredCategories.map((cat) => (
            <MenuItem
              key={cat}
              selected={cat === tx.category}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(cat);
              }}
            >
              {cat}
            </MenuItem>
          ))}
        </Menu>
      </TableCell>

      <TableCell sx={{ width: isMobile ? 150 : isCompact ? 180 : 240, border: 0, py: isCompact ? 1.25 : 1.5, px: isMobile ? 1 : 2 }}>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {tx.jars.length > 0 ? (
            tx.jars.map((jar) => (
              <Box
                key={jar.id}
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                  px: isMobile ? 1 : 1.25,
                  py: isMobile ? 0.35 : 0.45,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'rgba(4, 80, 58, 0.28)',
                  backgroundColor: 'rgba(255, 255, 255, 0.92)',
                  color: '#04503A',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: isMobile ? '0.68rem' : undefined }} noWrap>
                  {jar.name}
                </Typography>
              </Box>
            ))
          ) : (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: isMobile ? 1 : 1.25,
                py: isMobile ? 0.35 : 0.45,
                borderRadius: 999,
                backgroundColor: 'action.hover',
                color: 'text.secondary',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: isMobile ? '0.68rem' : undefined }}>
                No jar
              </Typography>
            </Box>
          )}
        </Stack>
      </TableCell>

      <TableCell
        align="right"
        sx={{
          color: tx.amount < 0 ? "text.primary" : "success.main",
          fontWeight: 700,
          width: isMobile ? 108 : 140,
          border: 0,
          py: isCompact ? 1.25 : 1.5,
          px: isMobile ? 1 : 2,
          fontSize: isMobile ? '0.85rem' : undefined,
        }}
      >
        {tx.amount < 0 ? "-" : "+"}${Math.abs(tx.amount).toFixed(2)}
      </TableCell>
    </TableRow>
  );
}
